import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "../Layout/Layout";
import { isAuthenticated, listOrders } from "../auth";
import { elakayAutoText } from "../pageInside/dataElakay";
import ButtonPagination from "../pageInside/ButtonPagination";
import PurchaseCard from "./purchased/PurchaseCard";
import OrderDetailsModal from "./purchased/OrderDetailsModal";
import DateRangeToolbar, { DEFAULT_DATE_FILTER, buildDateFilterParams } from "./purchased/DateRangeToolbar";
import "./purchased/PurchaseHistory.css";

const IN_PROGRESS_STATUSES = ["Not processed", "Processing", "Shipped"];
const PAGE_SIZE = 10;

// The list endpoint (GET /user/order/list) returns one row per (order, product) line item,
// already $unwind-ed server-side -- exactly like /c/orders's own grid. Grouping by orders_id here
// is new, page-local presentation logic (ticket: a Purchase Card represents one real order, which
// may hold several products), not a duplicate of anything already built.
const groupByOrder = (rows) => {
  const map = new Map();
  (rows || []).forEach((row) => {
    if (!row?.orders_id) return;
    const key = String(row.orders_id);
    if (!map.has(key)) {
      map.set(key, { orders_id: row.orders_id, createdAt: row.createdAt, amountTotal: row.amountTotal, items: [] });
    }
    map.get(key).items.push(row);
  });
  return Array.from(map.values());
};

// Computed only from whatever has actually been loaded -- never a guess beyond that (ticket:
// "ne fais pas de calcul incorrect").
const computeSummary = (groupedOrders) => {
  let totalSpent = 0;
  let delivered = 0;
  let inProgress = 0;
  groupedOrders.forEach((order) => {
    totalSpent += Number(order.amountTotal || 0);
    order.items.forEach((item) => {
      const status = item?.products?.status;
      if (status === "Delivered") delivered += 1;
      else if (IN_PROGRESS_STATUSES.includes(status)) inProgress += 1;
    });
  });
  return { totalPurchases: groupedOrders.length, totalSpent, delivered, inProgress };
};

const PurchasedPage = (props) => {
  const { infoSource } = props;
  const { token } = isAuthenticated();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOrder, setModalOrder] = useState(null); // {oid, pid, opid} | null

  const [dateFilter, setDateFilter] = useState(DEFAULT_DATE_FILTER);
  const [page, setPage] = useState(0);

  const fetchPage = useCallback(async (skip) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const fil = { orderStatus: "none", asc: dateFilter.asc, ...buildDateFilterParams(dateFilter) };
      const response = await listOrders(token, skip, 100, "_id", fil);
      if (response?.success === false || response?.error) {
        setError(response.error || elakayAutoText("h_9c122057d129b4", "Unable to load your purchase history."));
        return;
      }
      setRows((current) => (skip === 0 ? response.orders || [] : [...current, ...(response.orders || [])]));
      setTotalCount(response.totalCount?.[0]?.count || 0);
    } catch (err) {
      console.error("Failed to load purchase history:", err);
      setError(elakayAutoText("h_9c122057d129b4", "Unable to load your purchase history."));
    } finally {
      setLoading(false);
    }
  }, [token, dateFilter]);

  // Re-fetches from scratch whenever the filter/sort actually changes (fetchPage's identity
  // depends on them) -- always resets back to page 0, same as picking a fresh filter should.
  useEffect(() => {
    setPage(0);
    fetchPage(0);
  }, [fetchPage]);

  const groupedOrders = groupByOrder(rows);
  const summary = computeSummary(groupedOrders);
  const hasMoreOnServer = rows.length < totalCount;

  // Pagination is over GROUPED orders, not raw rows -- the list endpoint unwinds one row per
  // product, so paginating raw rows could split one multi-item order's products across two pages.
  const visibleOrders = groupedOrders.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const goToNextPage = useCallback(async () => {
    const needed = (page + 1) * PAGE_SIZE + 1;
    if (groupedOrders.length < needed && hasMoreOnServer) {
      await fetchPage(rows.length);
    }
    setPage((current) => current + 1);
  }, [page, groupedOrders.length, hasMoreOnServer, fetchPage, rows.length]);
  const goToPreviousPage = useCallback(() => {
    setPage((current) => Math.max(0, current - 1));
  }, []);

  const openOrderModal = useCallback((item) => {
    if (!item?.orders_id) return;
    setModalOrder({ oid: item.orders_id, pid: item.products?.productID, opid: item.products?._id });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("order", String(item.orders_id));
      return next;
    });
  }, [setSearchParams]);

  const closeOrderModal = useCallback(() => {
    setModalOrder(null);
    // Replace (not push) so dismissing the modal doesn't leave an extra history entry to Back
    // through -- Back while the modal is OPEN still naturally lands on the "no order=" entry that
    // opening it pushed, which the effect below reads as "close" (ticket §21's "Back ferme/revient
    // correctement").
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("order");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Keeps modal state in sync with the URL in both directions: restores it on a fresh load/refresh
  // when ?order=ID is already present, and closes it when the param disappears (Back). A specific
  // row's own click always already set the exact (pid, opid) via openOrderModal above -- this only
  // ever needs to *default* to the order's first item when arriving with just the bare order id.
  useEffect(() => {
    const orderParam = searchParams.get("order");
    if (!orderParam) {
      setModalOrder((current) => (current ? null : current));
      return;
    }
    setModalOrder((current) => {
      if (current && String(current.oid) === orderParam) return current;
      const match = groupedOrders.find((order) => String(order.orders_id) === orderParam);
      if (!match) return current;
      const firstItem = match.items[0];
      return { oid: match.orders_id, pid: firstItem.products?.productID, opid: firstItem.products?._id };
    });
    // groupedOrders is derived fresh every render from `rows`; re-run when the underlying data or
    // the URL actually changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, rows]);

  const renderSummary = () => {
    if (loading || groupedOrders.length === 0) return null;
    return (
      <div className="ph-summary">
        <div className="ph-summary-item">
          <span className="ph-summary-value">{summary.totalPurchases}</span>
          <span className="ph-summary-label">{elakayAutoText("h_6231e5c88f3eea", "Total Purchases")}</span>
        </div>
        <div className="ph-summary-item">
          <span className="ph-summary-value">${summary.totalSpent.toFixed(2)}</span>
          <span className="ph-summary-label">{elakayAutoText("h_ccf67dadebe37a", "Total Spent")}</span>
        </div>
        <div className="ph-summary-item">
          <span className="ph-summary-value">{summary.delivered}</span>
          <span className="ph-summary-label">{elakayAutoText("h_eea956cde875f0", "Delivered")}</span>
        </div>
        <div className="ph-summary-item">
          <span className="ph-summary-value">{summary.inProgress}</span>
          <span className="ph-summary-label">{elakayAutoText("h_f61eadaf153a7c", "In Progress")}</span>
        </div>
      </div>
    );
  };

  const renderToolbar = () => {
    // Stays visible even when the current filter matches zero orders ("No purchases yet") --
    // otherwise a customer who picks a narrow filter (e.g. Week) with no results would have no way
    // to switch back to a broader one. Only hidden during the very first load, before anything is
    // known yet.
    if (loading && rows.length === 0 && !error) return null;
    return (
      <DateRangeToolbar
        filter={dateFilter}
        onChange={setDateFilter}
        onReset={() => setDateFilter(DEFAULT_DATE_FILTER)}
      />
    );
  };

  const renderBody = () => {
    if (error) {
      return (
        <div className="ph-state ph-state-error">
          <p>{error}</p>
          <button type="button" className="ph-btn ph-btn-primary" onClick={() => fetchPage(0)}>{elakayAutoText("h_9f5cd8a2e8807d", "Retry")}</button>
        </div>
      );
    }

    if (!loading && groupedOrders.length === 0) {
      return (
        <div className="ph-state ph-state-empty">
          <p className="ph-state-title">{elakayAutoText("h_b7ecd4706d8b7e", "No purchases yet")}</p>
          <p className="ph-state-text">{elakayAutoText("h_296574a87296df", "When you buy something on Elakay, it'll show up here.")}</p>
          <Link to="/" className="ph-btn ph-btn-primary">{elakayAutoText("h_a1681a3ae451d4", "Explore Products")}</Link>
        </div>
      );
    }

    return (
      <>
        <div className="ph-card-list">
          {visibleOrders.map((order) => (
            <PurchaseCard
              key={order.orders_id}
              order={order}
              onViewProduct={() => {}}
              onOpenOrder={openOrderModal}
            />
          ))}
        </div>
        <ButtonPagination
          infoSource={infoSource}
          skip={page * PAGE_SIZE}
          limit={PAGE_SIZE}
          size={visibleOrders.length}
          siseProduct={hasMoreOnServer ? Number.MAX_SAFE_INTEGER : groupedOrders.length}
          loadMore={goToNextPage}
          loadMore0={goToPreviousPage}
        />
      </>
    );
  };

  return (
    <Layout
      infoSource={infoSource}
      title={elakayAutoText("h_1bd2d1a15a43e2", "Purchase History")}
      description={elakayAutoText("h_41682905073222", "Node React E-commerce App")}
      className="container-fluid"
      loading={loading}
      SliderPunShow={false}
      {...infoSource?.allSettings?.layout}
    >
      <div className="ph-page">
        <div className="ph-container">
          <div className="ph-header">
            <h1 className="ph-title">{elakayAutoText("h_1bd2d1a15a43e2", "Purchase History")}</h1>
            <p className="ph-subtitle">{elakayAutoText("h_529ea366893e8d", "Track your purchases, review order details and revisit your products.")}</p>
          </div>

          {renderSummary()}
          {renderToolbar()}
          {renderBody()}
        </div>
      </div>

      <OrderDetailsModal
        show={!!modalOrder}
        onClose={closeOrderModal}
        infoSource={infoSource}
        getOrderID={modalOrder}
      />
    </Layout>
  );
};

export default PurchasedPage;
